import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ImagePlus, Trash2 } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { PhotoError, useListingActions } from '../../../hooks/useListingAdmin';
import { ListingStatus } from '../../../types';
import { getCategory } from '../../../config/categories';
import { checkListingFields, LISTING_LIMITS, parseTags } from '../../../utils/listingEdit';
import { LISTING_STATUSES } from '../../../utils/superAdmin';
import { BilingualField, ErrorList, SaveBar, SectionTitle, SelectField, TextArea, useFormDraft } from './formFields';
import type { TabProps } from './ListingEditor';

/** Empty text → undefined, which removes the field instead of storing "". */
export function textOrDelete(value: string): string | undefined {
  const v = value.trim();
  return v ? v : undefined;
}

export default function InfoTab({ listing }: TabProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { updateListing, saving } = useListingActions();
  const isShul = listing.category === 'shul';
  const subcategories = getCategory(listing.category).subcategories;

  const { draft, update, dirty, reset, save, justSaved, failed } = useFormDraft({
    nameHe: listing.nameHe ?? '',
    nameEn: listing.nameEn ?? '',
    subcategory: 'subcategory' in listing ? listing.subcategory : '',
    descriptionHe: listing.descriptionHe ?? '',
    descriptionEn: listing.descriptionEn ?? '',
    noteHe: listing.noteHe ?? '',
    noteEn: listing.noteEn ?? '',
    tags: (listing.tags ?? []).join(', '),
  });
  const [errors, setErrors] = useState<string[]>([]);

  const handleSave = async () => {
    const problems = checkListingFields({ category: listing.category, ...draft });
    setErrors(problems);
    if (problems.length) return;
    const patch: Record<string, unknown> = {
      nameHe: draft.nameHe.trim(),
      nameEn: textOrDelete(draft.nameEn),
      descriptionHe: textOrDelete(draft.descriptionHe),
      descriptionEn: textOrDelete(draft.descriptionEn),
      noteHe: textOrDelete(draft.noteHe),
      noteEn: textOrDelete(draft.noteEn),
      tags: parseTags(draft.tags),
    };
    if (!isShul) patch.subcategory = draft.subcategory;
    // A mikvah's type is its subcategory (plan 6.2).
    if (listing.category === 'mikvah') patch['details.mikvahType'] = draft.subcategory;
    await save(() => updateListing(listing.id, patch));
  };

  return (
    <div className="space-y-5">
      <BilingualField
        labelHe={t('directory.field.nameHe')}
        labelEn={t('directory.field.nameEn')}
        valueHe={draft.nameHe}
        valueEn={draft.nameEn}
        onChangeHe={(v) => update({ nameHe: v })}
        onChangeEn={(v) => update({ nameEn: v })}
        maxLength={LISTING_LIMITS.nameLength}
        info={t('directory.admin.info.namesHint')}
      />

      {!isShul && (
        <SelectField
          label={t('directory.field.subcategory')}
          info={t('directory.admin.info.subcategoryHint')}
          value={draft.subcategory}
          onChange={(v) => update({ subcategory: v })}
          options={subcategories.map((s) => ({ value: s, label: t(`directory.sub.${s}`) }))}
        />
      )}

      <BilingualField
        labelHe={t('directory.field.descriptionHe')}
        labelEn={t('directory.field.descriptionEn')}
        valueHe={draft.descriptionHe}
        valueEn={draft.descriptionEn}
        onChangeHe={(v) => update({ descriptionHe: v })}
        onChangeEn={(v) => update({ descriptionEn: v })}
        multiline
        maxLength={LISTING_LIMITS.textLength}
      />

      <BilingualField
        labelHe={t('directory.field.noteHe')}
        labelEn={t('directory.field.noteEn')}
        valueHe={draft.noteHe}
        valueEn={draft.noteEn}
        onChangeHe={(v) => update({ noteHe: v })}
        onChangeEn={(v) => update({ noteEn: v })}
        maxLength={LISTING_LIMITS.noteLength}
        info={t('directory.admin.info.noteHint')}
        placeholderHe={t('directory.admin.info.notePlaceholderHe')}
        placeholderEn={t('directory.admin.info.notePlaceholderEn')}
      />

      <TextArea
        label={t('directory.field.tags')}
        info={t('directory.admin.info.tagsHint')}
        hint={t('directory.admin.info.tagsFormat')}
        value={draft.tags}
        onChange={(v) => update({ tags: v })}
        rows={2}
      />

      <ErrorList errors={errors} />
      <SaveBar
        dirty={dirty}
        saving={saving}
        onSave={handleSave}
        onCancel={() => {
          setErrors([]);
          reset();
        }}
        justSaved={justSaved}
        failed={failed}
      />

      {!isShul && <PhotoSection listingId={listing.id} photoUrl={listing.photoUrl} />}

      <StatusSection listingId={listing.id} status={listing.status} isSuperAdmin={user?.role === 'superAdmin'} />
    </div>
  );
}

function PhotoSection({ listingId, photoUrl }: { listingId: string; photoUrl?: string }) {
  const { t } = useTranslation();
  const { uploadPhoto, removePhoto, saving } = useListingActions();
  const fileRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<string | null>(null);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setMessage(null);
    try {
      await uploadPhoto(listingId, file);
    } catch (err) {
      setMessage(
        err instanceof PhotoError ? t(`directory.admin.info.${err.key}`) : t('directory.admin.saveFailed')
      );
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onRemove = async () => {
    if (!window.confirm(t('directory.admin.info.confirmRemovePhoto'))) return;
    setMessage(null);
    try {
      await removePhoto(listingId);
    } catch {
      setMessage(t('directory.admin.saveFailed'));
    }
  };

  return (
    <section className="space-y-3 pt-2 border-t border-ink/10">
      <SectionTitle>{t('directory.field.photo')}</SectionTitle>
      {photoUrl && (
        <img src={photoUrl} alt="" className="w-full max-w-xs aspect-[4/3] object-cover rounded-[16px] border border-ink/10" />
      )}
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          disabled={saving}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-[10px] border border-ink/20 text-sm text-ink hover:border-ink/50 disabled:opacity-40"
        >
          <ImagePlus className="w-4 h-4" aria-hidden />
          {saving
            ? t('directory.admin.saving')
            : photoUrl
              ? t('directory.admin.info.photoReplace')
              : t('directory.admin.info.photoUpload')}
        </button>
        {photoUrl && (
          <button
            type="button"
            disabled={saving}
            onClick={onRemove}
            className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-[10px] text-sm text-danger-600 hover:opacity-70 disabled:opacity-40"
          >
            <Trash2 className="w-4 h-4" aria-hidden />
            {t('directory.admin.info.photoRemove')}
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
      {message && <p className="text-sm text-danger-600">{message}</p>}
    </section>
  );
}

// 'deleted' is the owner's delete (it saves the status before); the super admin uses Bring back instead.
const ALL_STATUSES: ListingStatus[] = LISTING_STATUSES.filter((s) => s !== 'deleted');

function StatusSection({
  listingId,
  status,
  isSuperAdmin,
}: {
  listingId: string;
  status: ListingStatus;
  isSuperAdmin: boolean;
}) {
  const { t } = useTranslation();
  const { updateListing, deleteOwn, saving } = useListingActions();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(t('directory.admin.deleteOwn.confirm'))) return;
    setFailed(false);
    try {
      await deleteOwn({ id: listingId, status });
      navigate('/admin?item=&deleted=1');
    } catch {
      setFailed(true);
    }
  };

  const setStatus = async (next: ListingStatus) => {
    if (next === 'hidden' && !window.confirm(t('directory.admin.info.confirmHide'))) return;
    setFailed(false);
    try {
      await updateListing(listingId, { status: next });
    } catch {
      setFailed(true);
    }
  };

  return (
    <section className="space-y-3 pt-2 border-t border-ink/10">
      <SectionTitle>{t('directory.admin.info.statusTitle')}</SectionTitle>
      <p className="text-sm text-ink">
        {t('directory.admin.info.statusNow', { status: t(`directory.listingStatus.${status}`) })}
      </p>
      {isSuperAdmin ? (
        <SelectField
          label={t('directory.admin.info.changeStatus')}
          value={status}
          onChange={setStatus}
          options={(status === 'deleted' ? [...ALL_STATUSES, status] : ALL_STATUSES).map((s) => ({ value: s, label: t(`directory.listingStatus.${s}`) }))}
        />
      ) : status === 'published' ? (
        <button
          type="button"
          disabled={saving}
          onClick={() => setStatus('hidden')}
          className="min-h-[44px] px-4 rounded-[10px] border border-ink/20 text-sm text-ink hover:border-ink/50 disabled:opacity-40"
        >
          {t('directory.admin.info.hide')}
        </button>
      ) : status === 'hidden' ? (
        <button
          type="button"
          disabled={saving}
          onClick={() => setStatus('published')}
          className="min-h-[44px] px-4 rounded-[10px] bg-ink text-paper text-sm font-medium hover:opacity-90 disabled:opacity-40"
        >
          {t('directory.admin.info.publish')}
        </button>
      ) : (
        <p className="text-sm text-ash">{t('directory.admin.info.waitForApproval')}</p>
      )}
      {!isSuperAdmin && status !== 'deleted' && (
        <div className="pt-2 space-y-1">
          <button
            type="button"
            disabled={saving}
            onClick={handleDelete}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-[10px] border border-danger-600/40 text-sm text-danger-600 hover:border-danger-600 disabled:opacity-40"
          >
            <Trash2 className="w-4 h-4" aria-hidden />
            {t('directory.admin.deleteOwn.button')}
          </button>
          <p className="text-xs text-ash">{t('directory.admin.deleteOwn.hint')}</p>
        </div>
      )}
      {failed && <p className="text-sm text-danger-600">{t('directory.admin.saveFailed')}</p>}
    </section>
  );
}
